import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Calendar, ChevronDown } from 'lucide-react'
import './DateRangePicker.css'

/**
 * DateRangePicker Component
 *
 * A dropdown component for selecting date ranges with preset options
 * and custom date selection.
 *
 * @param {Object} props
 * @param {Object} props.value - Current value { startDate, endDate, preset }
 * @param {Function} props.onChange - Change handler
 * @param {Array} props.presets - Custom preset options
 * @param {boolean} props.showCustom - Show custom date inputs
 * @param {string} props.label - Optional label
 */
const DateRangePicker = ({
	value = {},
	onChange,
	presets = defaultPresets,
	showCustom = true,
	label,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [customStart, setCustomStart] = useState(value.startDate || '')
	const [customEnd, setCustomEnd] = useState(value.endDate || '')
	const dropdownRef = useRef(null)

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	// Handle preset selection
	const handlePresetSelect = (preset) => {
		const dates = calculateDateRange(preset.value)
		onChange({
			startDate: dates.startDate,
			endDate: dates.endDate,
			preset: preset.value,
		})
		setIsOpen(false)
	}

	// Handle custom date change
	const handleCustomChange = () => {
		if (customStart && customEnd) {
			onChange({
				startDate: customStart,
				endDate: customEnd,
				preset: 'custom',
			})
			setIsOpen(false)
		}
	}

	// Get display label
	const getDisplayLabel = () => {
		if (!value.preset && !value.startDate) {
			return 'Select date range'
		}

		if (value.preset && value.preset !== 'custom') {
			const preset = presets.find((p) => p.value === value.preset)
			return preset ? preset.label : value.preset
		}

		if (value.startDate && value.endDate) {
			return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`
		}

		return 'Select date range'
	}

	const activePreset = presets.find((p) => p.value === value.preset)

	return (
		<div className="date-range-picker" ref={dropdownRef}>
			{label && <label className="date-range-picker__label">{label}</label>}

			<button
				type="button"
				className={`date-range-picker__trigger ${isOpen ? 'date-range-picker__trigger--open' : ''}`}
				onClick={() => setIsOpen(!isOpen)}
			>
				<Calendar size={16} />
				<span className="date-range-picker__trigger-text">{getDisplayLabel()}</span>
				<ChevronDown size={16} className={`date-range-picker__chevron ${isOpen ? 'date-range-picker__chevron--open' : ''}`} />
			</button>

			{isOpen && (
				<div className="date-range-picker__dropdown">
					<div className="date-range-picker__presets">
						{presets.map((preset) => (
							<button
								key={preset.value}
								type="button"
								className={`date-range-picker__preset ${activePreset?.value === preset.value ? 'date-range-picker__preset--active' : ''}`}
								onClick={() => handlePresetSelect(preset)}
							>
								{preset.label}
							</button>
						))}
					</div>

					{showCustom && (
						<div className="date-range-picker__custom">
							<div className="date-range-picker__custom-label">Custom Range</div>
							<div className="date-range-picker__custom-inputs">
								<input
									type="date"
									value={customStart}
									onChange={(e) => setCustomStart(e.target.value)}
									className="date-range-picker__input"
									placeholder="Start date"
								/>
								<span className="date-range-picker__separator">to</span>
								<input
									type="date"
									value={customEnd}
									onChange={(e) => setCustomEnd(e.target.value)}
									className="date-range-picker__input"
									placeholder="End date"
								/>
							</div>
							<button
								type="button"
								className="date-range-picker__apply"
								onClick={handleCustomChange}
								disabled={!customStart || !customEnd}
							>
								Apply
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	)
}

// Default preset options
const defaultPresets = [
	{ value: 'today', label: 'Today' },
	{ value: 'yesterday', label: 'Yesterday' },
	{ value: 'last_7_days', label: 'Last 7 Days' },
	{ value: 'last_30_days', label: 'Last 30 Days' },
	{ value: 'this_month', label: 'This Month' },
	{ value: 'last_month', label: 'Last Month' },
	{ value: 'this_quarter', label: 'This Quarter' },
	{ value: 'last_quarter', label: 'Last Quarter' },
	{ value: 'this_year', label: 'This Year' },
	{ value: 'last_year', label: 'Last Year' },
]

// Calculate date range from preset
const calculateDateRange = (preset) => {
	const today = new Date()
	let startDate, endDate

	switch (preset) {
		case 'today':
			startDate = today
			endDate = today
			break
		case 'yesterday':
			startDate = new Date(today.setDate(today.getDate() - 1))
			endDate = startDate
			break
		case 'last_7_days':
			endDate = new Date()
			startDate = new Date(new Date().setDate(new Date().getDate() - 7))
			break
		case 'last_30_days':
			endDate = new Date()
			startDate = new Date(new Date().setDate(new Date().getDate() - 30))
			break
		case 'this_month':
			startDate = new Date(today.getFullYear(), today.getMonth(), 1)
			endDate = new Date()
			break
		case 'last_month':
			startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
			endDate = new Date(today.getFullYear(), today.getMonth(), 0)
			break
		case 'this_quarter':
			const currentQuarter = Math.floor(today.getMonth() / 3)
			startDate = new Date(today.getFullYear(), currentQuarter * 3, 1)
			endDate = new Date()
			break
		case 'last_quarter':
			const lastQuarter = Math.floor(today.getMonth() / 3) - 1
			const year = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear()
			const quarter = lastQuarter < 0 ? 3 : lastQuarter
			startDate = new Date(year, quarter * 3, 1)
			endDate = new Date(year, quarter * 3 + 3, 0)
			break
		case 'this_year':
			startDate = new Date(today.getFullYear(), 0, 1)
			endDate = new Date()
			break
		case 'last_year':
			startDate = new Date(today.getFullYear() - 1, 0, 1)
			endDate = new Date(today.getFullYear() - 1, 11, 31)
			break
		default:
			startDate = new Date()
			endDate = new Date()
	}

	return {
		startDate: formatDateISO(startDate),
		endDate: formatDateISO(endDate),
	}
}

// Format date to ISO string (YYYY-MM-DD)
const formatDateISO = (date) => {
	return date.toISOString().split('T')[0]
}

// Format date for display
const formatDate = (dateString) => {
	const date = new Date(dateString)
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

DateRangePicker.propTypes = {
	value: PropTypes.shape({
		startDate: PropTypes.string,
		endDate: PropTypes.string,
		preset: PropTypes.string,
	}),
	onChange: PropTypes.func.isRequired,
	presets: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
		})
	),
	showCustom: PropTypes.bool,
	label: PropTypes.string,
}

export default DateRangePicker
